/**
 * Phase C.7 — client-side subscribers for `case.updated` /
 * `matter.updated` / `client.updated` EventFrames.
 *
 * Mirrors `useDocumentUpdatedStream` (5.3.c / 5.5.a) — the plugin's
 * `handleCaseCreate` / `handleMatterCreate` / `handleClientDelete`
 * / `handleClientSetStatus` broadcast a typed event; this hook
 * filters by scope and invokes the caller's `onUpdate` when the
 * event matches.
 *
 * Consumers (non-exhaustive — grep for `useCaseUpdatedStream` /
 * `useMatterUpdatedStream` / `useClientUpdatedStream` for the
 * authoritative list):
 *   - `MatterContextTab` — refetches on `case.updated` (matter filter).
 *   - `ClientContextTab` — refetches on `matter.updated` + `client.updated`
 *     (client filter; skips `kind: "deleted"` to avoid NOT_FOUND races).
 *   - `PortfolioTimelineWidget` — refetches `matter.timeline` on
 *     `case.updated` (matter filter).
 *   - `RelationshipTimelineWidget` — refetches `client.timeline` on
 *     all three events (client filter) so every lifecycle kind
 *     lands in the timeline without a manual reload.
 *
 * Events ship from the gateway's `context.broadcast` primitive
 * (reaches every connected client). Each call site handles its
 * own filter; the hooks themselves are event-name-agnostic.
 */

import { useLexEventStream } from "@/hooks/useEventStream";

export interface ScopeUpdatedSignal {
  /** Action that triggered the event. Today the plugin emits only
   *  `"created"` from the create handlers; leaving the type open to
   *  future kinds (`"renamed"`, `"closed"`, `"deleted"`) with the
   *  `(string & {})` escape hatch so callers can narrow without
   *  blocking additions. */
  kind: "created" | (string & {});
  clientId?: string;
  matterId?: string;
  caseId?: string;
  /** Prior parentage on a `kind:"reassigned"` broadcast (the case's SOURCE
   *  client/matter). Lets a pane viewing the source scope drop the moved-away
   *  case live (else only the destination refreshes). */
  prevClientId?: string;
  prevMatterId?: string;
  [extra: string]: unknown;
}

/** Subscribe to `case.updated`. Fires on every broadcast; callers
 *  apply their own filter (e.g. matterId match). */
export function useCaseUpdatedStream(
  onUpdate: (signal: ScopeUpdatedSignal) => void,
): void {
  useLexEventStream("case.updated", (payload) => {
    const typed = payload as ScopeUpdatedSignal | null;
    if (!typed || typeof typed !== "object") return;
    onUpdate(typed);
  });
}

/** Subscribe to `matter.updated`. Fires on every broadcast; callers
 *  apply their own filter (e.g. clientId match). */
export function useMatterUpdatedStream(
  onUpdate: (signal: ScopeUpdatedSignal) => void,
): void {
  useLexEventStream("matter.updated", (payload) => {
    const typed = payload as ScopeUpdatedSignal | null;
    if (!typed || typeof typed !== "object") return;
    onUpdate(typed);
  });
}

/** Subscribe to `client.updated` (C.7.deferred). Emitted by
 *  `handleClientDelete` + `handleClientSetStatus`; payload includes
 *  `clientId` + the new `status` / `prevStatus`. Callers filter by
 *  `clientId` to avoid redundant reloads across org-mates. */
export function useClientUpdatedStream(
  onUpdate: (signal: ScopeUpdatedSignal) => void,
): void {
  useLexEventStream("client.updated", (payload) => {
    const typed = payload as ScopeUpdatedSignal | null;
    if (!typed || typeof typed !== "object") return;
    onUpdate(typed);
  });
}
